import { env } from '@/config/env';
import type { UserSelections } from '@/types/collection';
import type { UploadConsent } from '@/types/generation';

/**
 * Создание портрета или фотосессии.
 *
 * Тело — multipart: выбор в поле payload, снимки файлами в references.
 * Ровно эту форму разбирает Edge Function generate, поэтому
 * supabase.functions.invoke() здесь не используется — обычный fetch
 * с точным контрактом эндпоинта (тот же приём, что в services/generations.ts).
 *
 * Клиент не решает ничего: и выбор, и лимиты, и кредиты сервер проверяет
 * заново. Отсюда же и prompt не приходит — ни в ответе, ни в ошибке.
 */
export interface CreateGenerationInput {
  collectionSlug: string;
  selections: UserSelections;
  photos: File[];
  consent: UploadConsent;
  /** Код приглашения, по которому пришёл пользователь: реферала заводит сервер. */
  referralCode?: string | undefined;
  /** Сигнал ограничения стартовых начислений, не идентификация человека. */
  deviceHash?: string | undefined;
}

export interface SingleGenerationResult {
  kind: 'single';
  generationId: string;
  creditsLeft: number;
  /** false — работает mock-провайдер, и об этом нужно честно сказать. */
  realGeneration: boolean;
}

export interface PhotoshootResult {
  kind: 'photoshoot';
  photoshootId: string;
}

export type CreateGenerationResult = SingleGenerationResult | PhotoshootResult;

/** Ошибка, пригодная для показа: сообщение написал сервер, код — для ветвления интерфейса. */
export class GenerationRequestError extends Error {
  readonly code: string;
  readonly reason: string | undefined;

  constructor(message: string, code: string, reason?: string) {
    super(message);
    this.name = 'GenerationRequestError';
    this.code = code;
    this.reason = reason;
  }
}

interface ServerErrorBody {
  error?: { code?: string; message?: string; details?: { reason?: string } };
}

interface ServerSuccessBody {
  generationId?: string;
  photoshootId?: string;
  creditsLeft?: number;
  realGeneration?: boolean;
}

export async function createGeneration(
  input: CreateGenerationInput,
  accessToken: string,
): Promise<CreateGenerationResult> {
  const form = new FormData();

  form.append(
    'payload',
    JSON.stringify({
      collectionSlug: input.collectionSlug,
      selections: input.selections,
      consent: input.consent,
      // Ключи не появляются вовсе, если значения нет: сервер отличает
      // отсутствие кода от пустой строки.
      ...(input.referralCode ? { referralCode: input.referralCode } : {}),
      ...(input.deviceHash ? { deviceHash: input.deviceHash } : {}),
    }),
  );

  for (const photo of input.photos) {
    form.append('references', photo);
  }

  let response: Response;

  try {
    response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/generate`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
      },
      body: form,
    });
  } catch {
    throw new GenerationRequestError(
      'Нет связи с сервером. Проверьте интернет и попробуйте снова.',
      'NETWORK_ERROR',
    );
  }

  const body = (await response.json().catch(() => null)) as
    | (ServerErrorBody & ServerSuccessBody)
    | null;

  if (!body) {
    throw new GenerationRequestError(
      'Сервер ответил неожиданно. Попробуйте ещё раз.',
      'UNKNOWN_ERROR',
    );
  }

  if (!response.ok || body.error) {
    throw new GenerationRequestError(
      body.error?.message ?? 'Не удалось создать портрет. Попробуйте ещё раз.',
      body.error?.code ?? 'UNKNOWN_ERROR',
      body.error?.details?.reason,
    );
  }

  if (body.photoshootId) {
    return { kind: 'photoshoot', photoshootId: body.photoshootId };
  }

  if (!body.generationId) {
    throw new GenerationRequestError(
      'Сервер ответил неожиданно. Попробуйте ещё раз.',
      'UNKNOWN_ERROR',
    );
  }

  return {
    kind: 'single',
    generationId: body.generationId,
    creditsLeft: body.creditsLeft ?? 0,
    realGeneration: body.realGeneration ?? false,
  };
}
